(() => {
  const configElement = document.currentScript;
  if (!configElement) return;

  const splitUrls = (value) =>
    (value || '')
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean);

  const assetPromises = new Map();

  const normalizeUrl = (url) => new URL(url, document.baseURI).href;

  const loadStyle = (url) => {
    const normalizedUrl = normalizeUrl(url);
    const key = `style:${normalizedUrl}`;
    if (assetPromises.has(key)) return assetPromises.get(key);

    const promise = new Promise((resolve, reject) => {
      const existingLink = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find(
        (link) => normalizeUrl(link.href) === normalizedUrl
      );

      if (existingLink) {
        if (existingLink.sheet) {
          resolve();
          return;
        }
        existingLink.addEventListener('load', resolve, { once: true });
        existingLink.addEventListener('error', () => reject(new Error(`Unable to load ${normalizedUrl}`)), {
          once: true,
        });
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = normalizedUrl;
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', () => reject(new Error(`Unable to load ${normalizedUrl}`)), { once: true });
      document.head.appendChild(link);
    });

    assetPromises.set(key, promise);
    return promise;
  };

  const loadScript = (url) => {
    const normalizedUrl = normalizeUrl(url);
    const key = `script:${normalizedUrl}`;
    if (assetPromises.has(key)) return assetPromises.get(key);

    const promise = new Promise((resolve, reject) => {
      const existingScript = Array.from(document.scripts).find(
        (script) => script.src && normalizeUrl(script.src) === normalizedUrl
      );

      if (existingScript) {
        if (existingScript.dataset.deferredUiLoaded === 'true') {
          resolve();
          return;
        }
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', () => reject(new Error(`Unable to load ${normalizedUrl}`)), {
          once: true,
        });
        return;
      }

      const script = document.createElement('script');
      script.src = normalizedUrl;
      script.async = false;
      script.addEventListener(
        'load',
        () => {
          script.dataset.deferredUiLoaded = 'true';
          resolve();
        },
        { once: true }
      );
      script.addEventListener('error', () => reject(new Error(`Unable to load ${normalizedUrl}`)), { once: true });
      document.head.appendChild(script);
    });

    assetPromises.set(key, promise);
    return promise;
  };

  const createGroupLoader = (name, styles, scripts) => {
    let groupPromise;
    let state = 'idle';

    const load = () => {
      if (groupPromise) return groupPromise;

      state = 'loading';
      document.documentElement.dataset[`${name}Assets`] = state;
      const stylesPromise = Promise.all(styles.map(loadStyle));
      const scriptsPromise = scripts.reduce(
        (promise, scriptUrl) => promise.then(() => loadScript(scriptUrl)),
        Promise.resolve()
      );

      groupPromise = Promise.all([stylesPromise, scriptsPromise])
        .then(() => {
          state = 'loaded';
          document.documentElement.dataset[`${name}Assets`] = state;
        })
        .catch((error) => {
          state = 'error';
          document.documentElement.dataset[`${name}Assets`] = state;
          groupPromise = undefined;
          throw error;
        });

      return groupPromise;
    };

    return {
      load,
      isLoaded: () => state === 'loaded',
    };
  };

  const cartGroup = createGroupLoader(
    'cart',
    splitUrls(configElement.dataset.cartStyles),
    splitUrls(configElement.dataset.cartScripts)
  );
  const searchGroup = createGroupLoader(
    'search',
    splitUrls(configElement.dataset.searchStyles),
    splitUrls(configElement.dataset.searchScripts)
  );

  window.Torras = window.Torras || {};
  window.Torras.updateCloneHeaderCartBadge =
    window.Torras.updateCloneHeaderCartBadge ||
    ((itemCount) => {
      const count = Number(itemCount) || 0;
      const shouldShow = count > 0 && count < 100;

      document.querySelectorAll('.clone-header__cart').forEach((button) => {
        const icon = button.querySelector('.clone-header__cart-icon');
        let badge = button.querySelector('.clone-header__cart-badge');

        if (!shouldShow) {
          if (badge) badge.remove();
          return;
        }

        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'clone-header__cart-badge';
          badge.setAttribute('aria-hidden', 'true');
          if (icon) {
            icon.insertAdjacentElement('afterend', badge);
          } else {
            button.appendChild(badge);
          }
        }

        badge.textContent = String(count);
      });
    });

  window.TorrasDeferredUI = {
    loadCart: cartGroup.load,
    loadSearch: searchGroup.load,
    isCartLoaded: cartGroup.isLoaded,
    isSearchLoaded: searchGroup.isLoaded,
  };

  const cartOpenSelector = '[data-cart-drawer-trigger], #cart-icon-bubble';
  const cartIntentSelector = `${cartOpenSelector}, product-form button[type="submit"][name="add"]`;
  const searchTriggerSelector = '.header__search summary';

  const getElement = (target) => (target instanceof Element ? target : null);

  const warmAssets = (event) => {
    const target = getElement(event.target);
    if (!target) return;

    if (target.closest(cartIntentSelector)) {
      cartGroup.load().catch(() => {});
    }
    if (target.closest(searchTriggerSelector)) {
      searchGroup.load().catch(() => {});
    }
  };

  document.addEventListener('pointerover', warmAssets, { passive: true, capture: true });
  document.addEventListener('pointerdown', warmAssets, { passive: true, capture: true });
  document.addEventListener('touchstart', warmAssets, { passive: true, capture: true });
  document.addEventListener('focusin', warmAssets, { passive: true, capture: true });

  document.addEventListener(
    'click',
    (event) => {
      const target = getElement(event.target);
      if (!target) return;

      const cartTrigger = target.closest(cartOpenSelector);
      if (cartTrigger && !cartGroup.isLoaded()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        cartGroup
          .load()
          .then(() => {
            if (cartTrigger.isConnected) cartTrigger.click();
          })
          .catch(() => {
            window.location.assign(configElement.dataset.cartFallback || window.routes.cart_url);
          });
        return;
      }

      const searchTrigger = target.closest(searchTriggerSelector);
      if (searchTrigger && !searchGroup.isLoaded()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        searchGroup
          .load()
          .then(() => {
            if (searchTrigger.isConnected) searchTrigger.click();
          })
          .catch(() => {
            window.location.assign(configElement.dataset.searchFallback || '/search');
          });
      }
    },
    true
  );
})();
